import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useEnquiry } from "@/context/EnquiryContext";
import { submitEnquiry, uploadResume } from "@/lib/api";
import {
  INTEREST_OPTIONS, USER_TYPES, CONTACT_METHODS, CONTACT_TIMES, CAREER_INTERESTS,
} from "@/config/site";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, X, User, Briefcase, PhoneCall, Upload } from "lucide-react";

const initial = {
  full_name: "", phone: "", email: "",
  preferred_contact_method: "", interest_category: "Atmasamman Group",
  user_type: "", message: "",
  city: "", organization_name: "", current_role: "", preferred_contact_time: "",
  highest_qualification: "", years_of_experience: "", linkedin: "",
  marketing_opt_in: false, consent: false, website: "",
};

function getUTM() {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || undefined,
    utm_medium: p.get("utm_medium") || undefined,
    utm_campaign: p.get("utm_campaign") || undefined,
    utm_content: p.get("utm_content") || undefined,
    utm_term: p.get("utm_term") || undefined,
  };
}

const labelCls = "block text-sm font-medium text-slate-700 mb-1.5";
const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition";
const selectCls = inputCls + " appearance-none bg-white";
const sectionTitle = "font-mono text-xs uppercase tracking-[0.2em] text-orange-500 font-semibold flex items-center gap-2 mb-3";

export function EnquiryModal() {
  const { open, prefill, closeEnquiry } = useEnquiry();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [errors, setErrors] = useState({});
  const [resumeName, setResumeName] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ ...initial, interest_category: prefill.interest || "Atmasamman Group" });
      setSuccess(null);
      setErrors({});
      setResumeName("");
    }
  }, [open, prefill]);

  const isCareer = CAREER_INTERESTS.includes(form.interest_category);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = "Please enter your full name";
    const digits = form.phone.replace(/[^\d]/g, "");
    if (digits.length < 7 || digits.length > 15) e.phone = "Please enter a valid phone number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email";
    if (!form.preferred_contact_method) e.preferred_contact_method = "Select a contact method";
    if (!form.interest_category) e.interest_category = "Select an option";
    if (!form.user_type) e.user_type = "Select who you are";
    if (form.message.trim().length < 1) e.message = "Please share your requirement";
    if (!form.consent) e.consent = "Consent is required to proceed";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onResume = async (file) => {
    if (!file) return;
    const okTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!okTypes.includes(file.type)) return toast.error("Only PDF, DOC or DOCX files are allowed");
    if (file.size > 5 * 1024 * 1024) return toast.error("File too large (max 5MB)");
    try {
      const { resume_path } = await uploadResume(file);
      set("resume_path", resume_path);
      setResumeName(file.name);
      toast.success("Resume attached");
    } catch {
      toast.error("Could not upload resume. Please try again.");
    }
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) {
      toast.error("Please complete the required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        source_page: prefill.sourcePage || window.location.pathname,
        ...getUTM(),
      };
      const res = await submitEnquiry(payload);
      setSuccess({ first_name: res.first_name || form.full_name.split(" ")[0], email: res.email || form.email });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.error("This enquiry was just submitted. Please wait a moment.");
      else if (status === 429) toast.error("Too many requests. Please try again shortly.");
      else toast.error("Your enquiry could not be submitted. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeEnquiry()}>
      <DialogContent
        className="max-w-2xl gap-0 overflow-hidden border-0 bg-white p-0 sm:rounded-2xl"
        data-testid="enquiry-modal"
      >
        <DialogTitle className="sr-only">Request Information from Atmasamman Group</DialogTitle>
        <DialogDescription className="sr-only">Submit your enquiry and our team will contact you via your preferred channel.</DialogDescription>
        {/* header */}
        <div className="grad-navy relative px-6 py-5">
          <button onClick={closeEnquiry} data-testid="enquiry-close-btn" className="absolute right-4 top-4 text-white/70 hover:text-white">
            <X className="h-5 w-5" />
          </button>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-orange-400">Atmasamman Group</p>
          <h2 className="mt-1 font-display text-xl font-bold text-white sm:text-2xl">
            {success ? "Enquiry Received" : "Request Information"}
          </h2>
          {!success && <p className="mt-1 text-sm text-white/70">Tell us what you're looking for — we'll reach out via your preferred channel.</p>}
        </div>

        {success ? (
          <div className="px-6 py-10 text-center" data-testid="enquiry-success">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-9 w-9 text-green-600" />
            </div>
            <h3 className="font-display text-2xl font-bold text-slate-900">Thank You, {success.first_name}!</h3>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-600">
              We've received your enquiry successfully. A confirmation email has been sent to <span className="font-semibold text-slate-900">{success.email}</span>. Our team will contact you based on your selected contact preference.
            </p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/organizations" onClick={closeEnquiry} data-testid="success-explore-orgs" className="w-full rounded-xl bg-orange-500 px-6 py-3 text-sm font-semibold text-white hover:bg-orange-600 sm:w-auto">
                Explore Our Organizations
              </Link>
              <Link to="/" onClick={closeEnquiry} data-testid="success-back-home" className="w-full rounded-xl border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto">
                Back to Home
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="max-h-[70vh] overflow-y-auto px-6 py-6" data-testid="enquiry-form" noValidate>
            {/* honeypot */}
            <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />

            {/* Contact information */}
            <p className={sectionTitle}><User className="h-3.5 w-3.5" /> Contact Information</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="full_name">Full Name *</label>
                <input id="full_name" className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Your full name" data-testid="enquiry-full-name" />
                {errors.full_name && <p className="mt-1 text-xs text-red-600" data-testid="error-full-name">{errors.full_name}</p>}
              </div>
              <div>
                <label className={labelCls} htmlFor="phone">Phone Number *</label>
                <input id="phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 XXXXX XXXXX" data-testid="enquiry-phone" />
                {errors.phone && <p className="mt-1 text-xs text-red-600" data-testid="error-phone">{errors.phone}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="email">Email Address *</label>
                <input id="email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" data-testid="enquiry-email" />
                {errors.email && <p className="mt-1 text-xs text-red-600" data-testid="error-email">{errors.email}</p>}
              </div>
            </div>

            {/* Your requirement */}
            <p className={`${sectionTitle} mt-6`}><Briefcase className="h-3.5 w-3.5" /> Your Requirement</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="interest">I Am Interested In *</label>
                <select id="interest" className={selectCls} value={form.interest_category} onChange={(e) => set("interest_category", e.target.value)} data-testid="enquiry-interest">
                  {INTEREST_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="user_type">User Type *</label>
                <select id="user_type" className={selectCls} value={form.user_type} onChange={(e) => set("user_type", e.target.value)} data-testid="enquiry-user-type">
                  <option value="">Select one</option>
                  {USER_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                {errors.user_type && <p className="mt-1 text-xs text-red-600" data-testid="error-user-type">{errors.user_type}</p>}
              </div>
            </div>

            {isCareer && (
              <div className="mt-4 grid grid-cols-1 gap-4 rounded-xl border border-orange-200 bg-orange-50/50 p-4 sm:grid-cols-2" data-testid="career-fields">
                <div>
                  <label className={labelCls} htmlFor="qual">Highest Qualification</label>
                  <input id="qual" className={inputCls} value={form.highest_qualification} onChange={(e) => set("highest_qualification", e.target.value)} placeholder="e.g. B.Tech, MCA" data-testid="enquiry-qualification" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="exp">Years of Experience</label>
                  <input id="exp" className={inputCls} value={form.years_of_experience} onChange={(e) => set("years_of_experience", e.target.value)} placeholder="e.g. Fresher, 2 years" data-testid="enquiry-experience" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="linkedin">LinkedIn Profile</label>
                  <input id="linkedin" className={inputCls} value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} placeholder="https://linkedin.com/in/..." data-testid="enquiry-linkedin" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Resume (optional, PDF/DOC/DOCX, max 5MB)</label>
                  <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-600 hover:border-orange-400" data-testid="enquiry-resume-label">
                    <Upload className="h-4 w-4 text-orange-500" />
                    <span>{resumeName || "Upload your resume"}</span>
                    <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => onResume(e.target.files?.[0])} data-testid="enquiry-resume-input" />
                  </label>
                </div>
              </div>
            )}

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="city">City / Location</label>
                <input id="city" className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Optional" data-testid="enquiry-city" />
              </div>
              <div>
                <label className={labelCls} htmlFor="org">Organization / Company Name</label>
                <input id="org" className={inputCls} value={form.organization_name} onChange={(e) => set("organization_name", e.target.value)} placeholder="Optional" data-testid="enquiry-organization" />
              </div>
              {!isCareer && (
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="role">Current Role / Job Title</label>
                  <input id="role" className={inputCls} value={form.current_role} onChange={(e) => set("current_role", e.target.value)} placeholder="Optional" data-testid="enquiry-role" />
                </div>
              )}
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="message">Message / Requirement *</label>
                <textarea id="message" rows={3} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us a little about what you need" data-testid="enquiry-message" />
                {errors.message && <p className="mt-1 text-xs text-red-600" data-testid="error-message">{errors.message}</p>}
              </div>
            </div>

            {/* Contact preference */}
            <p className={`${sectionTitle} mt-6`}><PhoneCall className="h-3.5 w-3.5" /> Contact Preference</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="method">Preferred Contact Method *</label>
                <select id="method" className={selectCls} value={form.preferred_contact_method} onChange={(e) => set("preferred_contact_method", e.target.value)} data-testid="enquiry-contact-method">
                  <option value="">Select one</option>
                  {CONTACT_METHODS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                {errors.preferred_contact_method && <p className="mt-1 text-xs text-red-600" data-testid="error-contact-method">{errors.preferred_contact_method}</p>}
              </div>
              <div>
                <label className={labelCls} htmlFor="time">Preferred Time to Contact</label>
                <select id="time" className={selectCls} value={form.preferred_contact_time} onChange={(e) => set("preferred_contact_time", e.target.value)} data-testid="enquiry-contact-time">
                  <option value="">Any Time</option>
                  {CONTACT_TIMES.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>

            {/* consent */}
            <div className="mt-6 space-y-3">
              <label className="flex items-start gap-2.5 text-sm text-slate-600">
                <input type="checkbox" checked={form.marketing_opt_in} onChange={(e) => set("marketing_opt_in", e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500" data-testid="enquiry-marketing" />
                <span>I would like to receive relevant updates from Atmasamman Group.</span>
              </label>
              <label className="flex items-start gap-2.5 text-sm text-slate-700">
                <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500" data-testid="enquiry-consent" />
                <span>I agree that Atmasamman Group may contact me regarding my enquiry. *</span>
              </label>
              {errors.consent && <p className="text-xs text-red-600" data-testid="error-consent">{errors.consent}</p>}
            </div>

            <button type="submit" disabled={submitting} data-testid="enquiry-submit-btn"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-orange-600 disabled:opacity-70">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : "SUBMIT ENQUIRY"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
